import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import Swal from "sweetalert2";
import { postDataToAPI } from "../Shared/shared";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const UserDataGrid = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [registrationFilter, setRegistrationFilter] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // ✅ Fetch users
  const fetchAllUsers = async () => {
    try {
      const response = await postDataToAPI(
        "/dashboard/fetchAllUsers.php",
        {}
      );
      setUsers(response || []);
      setFilteredUsers(response || []);
    } catch {
      setErrorMessage("Error fetching users.");
    }
  };

  useEffect(() => {
    fetchAllUsers();
  }, []);

  // ✅ Activate / Deactivate
  const handleUserStatusChange = async (id, status, email) => {
    const newStatus = status === "Active" ? "Inactive" : "Active";

    const result = await Swal.fire({
      title: `Are you sure you want to ${
        newStatus === "Active" ? "activate" : "deactivate"
      } this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
    });

    if (!result.isConfirmed) return;

    try {
      const formData = new FormData();
      formData.append("id", id);
      formData.append("status", newStatus);

      const response = await postDataToAPI(
        "/dashboard/updateUserStatus.php",
        formData
      );

      const emailData = new FormData();
      emailData.append(
        "action",
        newStatus === "Active" ? "activateUser" : "deactivateUser"
      );
      emailData.append("email", email);

      await postDataToAPI("/Emails/sendEmails.php", emailData);

      if (response === "Success") {
        fetchAllUsers();

        Swal.fire(
          "Success",
          `User ${newStatus === "Active" ? "activated" : "deactivated"}`,
          "success"
        );
      } else {
        throw new Error();
      }
    } catch {
      Swal.fire("Error", "Failed to update user status", "error");
    }
  };

  // ✅ Apply filters
  const applyFilters = (text, regType) => {
    let filtered = users;

    if (text) {
      filtered = filtered.filter((user) =>
        [user.fullName, user.email, user.status]
          .join(" ")
          .toLowerCase()
          .includes(text.toLowerCase())
      );
    }

    if (regType) {
      filtered = filtered.filter(
        (user) => user.registrationType === regType
      );
    }

    setFilteredUsers(filtered);
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilterText(value);
    applyFilters(value, registrationFilter);
  };

  const handleRegistrationFilter = (e) => {
    const value = e.target.value;
    setRegistrationFilter(value);
    applyFilters(filterText, value);
  };

  // ✅ CSV Export
  const exportToCSV = () => {
    if (filteredUsers.length === 0) {
      Swal.fire("No Data", "Nothing to export", "info");
      return;
    }

    const headers = [
      "Full Name",
      "Phone",
      "Email",
      "Type",
      "Status",
    ];

    const rows = filteredUsers.map((u) => [
      u.fullName,
      u.phoneNumber,
      u.email,
      u.registrationType,
      u.status,
    ]);

    const csv =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows].map((r) => r.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "users.csv";
    link.click();
  };

  // ✅ PDF Export
  const exportToPDF = () => {
    if (filteredUsers.length === 0) {
      Swal.fire("No data", "Nothing to export", "info");
      return;
    }

    const doc = new jsPDF();

    const columns = [
      "NO",
      "Full Name",
      "Phone",
      "Email",
      "Type",
      "Status",
    ];

    const rows = filteredUsers.map((u, index) => [
      index + 1,
      u.fullName,
      u.phoneNumber,
      u.email,
      u.registrationType,
      u.status,
    ]);

    doc.text("Users Report", 14, 15);

    autoTable(doc, {
      startY: 20,
      head: [columns],
      body: rows,
    });

    doc.save("users.pdf");
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2>Users</h2>
      <Link to="/adminDashboard">Back</Link>

      {/* Error */}
      {errorMessage && (
        <Typography color="error">{errorMessage}</Typography>
      )}

      {/* Search */}
      <TextField
        label="Search users"
        fullWidth
        margin="normal"
        value={filterText}
        onChange={handleFilterChange}
      />

      {/* Registration filter */}
      <TextField
        select
        label="Filter by Registration Type"
        fullWidth
        margin="normal"
        value={registrationFilter}
        onChange={handleRegistrationFilter}
      >
        <MenuItem value="">All</MenuItem>
        <MenuItem value="chef">Chef</MenuItem>
        <MenuItem value="home-owner">Home Owner</MenuItem>
        <MenuItem value="house-help">House Help</MenuItem>
      </TextField>

      {/* Export buttons */}
      <Button
        variant="contained"
        onClick={exportToCSV}
        style={{ marginRight: "10px" }}
      >
        Export CSV
      </Button>

      <Button
        variant="contained"
        color="secondary"
        onClick={exportToPDF}
      >
        Export PDF
      </Button>

      {/* Table */}
      <TableContainer component={Paper} style={{ marginTop: "20px" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">Full Name</TableCell>
              <TableCell align="center">Phone</TableCell>
              <TableCell align="center">Email</TableCell>
              <TableCell align="center">Type</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell align="center">{user.fullName}</TableCell>
                  <TableCell align="center">
                    {user.phoneNumber}
                  </TableCell>
                  <TableCell align="center">{user.email}</TableCell>
                  <TableCell align="center">
                    {user.registrationType}
                  </TableCell>
                  <TableCell align="center">{user.status}</TableCell>

                  {/* Action */}
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color={
                        user.status === "Active"
                          ? "secondary"
                          : "primary"
                      }
                      onClick={() =>
                        handleUserStatusChange(
                          user.id,
                          user.status,
                          user.email
                        )
                      }
                    >
                      {user.status === "Active"
                        ? "Deactivate"
                        : "Activate"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default UserDataGrid;